/**
 * EVENTSFLO-PRE-DEPLOY-CLEANUP-AUDIT-PANEL-V1
 *
 * READ-ONLY SuperAdmin card showing the most recent
 * `pre_deploy_cleanup_audit` rows. Refresh only — no destructive
 * controls (no Delete, Rollback, Re-run, Apply, Override, Edit).
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import api from '../../../lib/api';

const fmtTs = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

export default function PreDeployCleanupHistoryCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [exporting, setExporting] = useState(false);

  // EVENTSFLO-ADMIN-AUDIT-PANELS-CSV-EXPORT-V1
  // SuperAdmin-only CSV export — same projection and 10-row limit
  // as the panel above. Read-only.
  const downloadCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get(
        '/admin/maintenance/pre-deploy-cleanup/audit-log/export.csv',
        { params: { limit: 10 }, responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      let filename = 'pre_deploy_cleanup_audit.csv';
      const cd = r.headers?.['content-disposition'] || '';
      const m = /filename="([^"]+)"/.exec(cd);
      if (m && m[1]) filename = m[1];
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || 'Failed to download CSV.';
      toast.error(String(msg));
    } finally {
      setExporting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(
        '/admin/maintenance/pre-deploy-cleanup/audit-log',
        { params: { limit: 10 } },
      );
      setRows(Array.isArray(r.data?.rows) ? r.data.rows : []);
      setGeneratedAt(r.data?.generated_at || null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || 'Failed to load cleanup history.';
      toast.error(String(msg));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <Card
      id="pre-deploy-cleanup-history-panel"
      data-testid="pre-deploy-cleanup-history-card"
      className="border-zinc-800"
    >
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle data-testid="pre-deploy-cleanup-history-title">
            Cleanup history
          </CardTitle>
          <p
            className="text-xs text-muted-foreground mt-1"
            data-testid="pre-deploy-cleanup-history-readonly-note"
          >
            Read-only history of recent pre-deploy TEST data cleanup runs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="pre-deploy-cleanup-history-refresh-btn"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={downloadCsv}
            disabled={exporting || loading}
            data-testid="pre-deploy-cleanup-history-download-csv-btn"
          >
            {exporting ? 'Exporting…' : 'Download CSV'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {(!rows || rows.length === 0) && !loading && (
          <div
            className="text-xs text-muted-foreground py-4"
            data-testid="pre-deploy-cleanup-history-empty"
          >
            No cleanup runs found.
          </div>
        )}
        {rows && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table
              className="w-full text-xs"
              data-testid="pre-deploy-cleanup-history-table"
            >
              <thead className="text-muted-foreground">
                <tr className="text-left">
                  <th className="py-1">When</th>
                  <th>Actor</th>
                  <th>Run id</th>
                  <th>Scanned</th>
                  <th>Candidates</th>
                  <th>Deleted</th>
                  <th>Skipped</th>
                  <th>Warn</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.cleanup_run_id || i}
                    className="border-t border-zinc-800"
                    data-testid={`pre-deploy-cleanup-history-row-${i}`}
                  >
                    <td className="py-1">{fmtTs(r.created_at)}</td>
                    <td>{r.actor_email || r.actor_user_id || '—'}</td>
                    <td className="font-mono text-[10px] break-all">
                      {r.cleanup_run_id || '—'}
                    </td>
                    <td className="text-center">
                      {r.scanned_collections_count}
                    </td>
                    <td className="text-center">
                      {r.total_candidates > 0 ? (
                        <Badge variant="outline" className="text-[10px]">
                          {r.total_candidates}
                        </Badge>
                      ) : r.total_candidates}
                    </td>
                    <td className="text-center">
                      {r.total_deleted > 0 ? (
                        <Badge variant="destructive" className="text-[10px]">
                          {r.total_deleted}
                        </Badge>
                      ) : r.total_deleted}
                    </td>
                    <td className="text-center">{r.total_skipped}</td>
                    <td className="text-center">{r.warnings_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {generatedAt && (
          <div className="text-[10px] text-muted-foreground mt-2 text-right">
            Generated at {fmtTs(generatedAt)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
